import type { AlertThresholds, BudgetCategoryStatus } from '../types/budget.types';
import type { OnTrackStatus } from '../types/savings-goal.types';

export function calculatePercentUsed(spent: number, budget: number): number {
  if (budget <= 0) return spent > 0 ? 100 : 0;
  return Math.round((spent / budget) * 100);
}

export function deriveCategoryStatus(
  percentUsed: number,
  thresholds: AlertThresholds
): BudgetCategoryStatus {
  if (percentUsed > thresholds.limit) return 'over';
  if (percentUsed >= thresholds.critical) return 'critical';
  if (percentUsed >= thresholds.warning) return 'warning';
  return 'on_track';
}

/** Derive status for a category limit; zero-plan limits are treated as unset, not over-budget. */
export function deriveCategoryLimitMetrics(
  spent: number,
  planAmount: number,
  viewAmount: number,
  thresholds: AlertThresholds
): {
  percentUsed: number;
  status: BudgetCategoryStatus;
  statusLabel: string;
  countsTowardTotals: boolean;
} {
  if (planAmount <= 0) {
    return {
      percentUsed: 0,
      status: 'on_track',
      statusLabel: 'Not set',
      countsTowardTotals: false,
    };
  }
  const percentUsed = calculatePercentUsed(spent, viewAmount);
  const status = deriveCategoryStatus(percentUsed, thresholds);
  return {
    percentUsed,
    status,
    statusLabel: deriveStatusLabel(status, percentUsed),
    countsTowardTotals: true,
  };
}

export function deriveStatusLabel(status: BudgetCategoryStatus, percentUsed: number): string {
  switch (status) {
    case 'over':
      return `${percentUsed}% Over`;
    case 'critical':
      return `${percentUsed}% Used`;
    case 'warning':
      return `${percentUsed}% Used`;
    default:
      return percentUsed >= 75 ? `${percentUsed}% Used` : 'On Track';
  }
}

export function sumSpentForCategories(
  spentByCategoryId: Map<string, number>,
  categoryId: string,
  descendantIds: string[] = []
): number {
  let total = spentByCategoryId.get(categoryId) ?? 0;
  for (const id of descendantIds) {
    total += spentByCategoryId.get(id) ?? 0;
  }
  return total;
}

export function calculateGoalProgress(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((current / target) * 100));
}

export function calculateDaysRemaining(targetDate: string, today: Date = new Date()): number {
  const target = new Date(targetDate);
  const diff = target.getTime() - today.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function deriveGoalOnTrackStatus(
  currentAmount: number,
  targetAmount: number,
  createdAt: string,
  targetDate: string,
  today: Date = new Date()
): OnTrackStatus {
  const start = new Date(createdAt);
  const end = new Date(targetDate);
  const totalMs = end.getTime() - start.getTime();
  if (totalMs <= 0) return currentAmount >= targetAmount ? 'on_track' : 'at_risk';

  const elapsedMs = Math.min(Math.max(today.getTime() - start.getTime(), 0), totalMs);
  const timeRatio = elapsedMs / totalMs;
  const progressRatio = targetAmount > 0 ? currentAmount / targetAmount : 0;

  return progressRatio >= timeRatio ? 'on_track' : 'at_risk';
}

export const BILL_DUE_SOON_DAYS = 7;
/** Days after due date before an autopay bill triggers a review reminder. */
export const AUTOPAY_GRACE_PERIOD_DAYS = 3;
/** Autopay bills due within this many days get an informational reminder. */
export const AUTOPAY_DUE_SOON_DAYS = 5;

export function getBillDaysUntilDue(dueDate: string, today: Date = new Date()): number {
  const due = new Date(dueDate);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dueStart = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  return Math.round((dueStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
}

/** True when unpaid and due date is overdue or within the next `withinDays` calendar days. */
export function isBillDueSoon(
  dueDate: string,
  status: string,
  withinDays: number = BILL_DUE_SOON_DAYS,
  today: Date = new Date()
): boolean {
  if (status === 'paid') return false;
  return getBillDaysUntilDue(dueDate, today) <= withinDays;
}

/** True when an autopay bill is past the grace period following its due date. */
export function isAutopayPastGrace(
  dueDate: string,
  graceDays: number = AUTOPAY_GRACE_PERIOD_DAYS,
  today: Date = new Date()
): boolean {
  return getBillDaysUntilDue(dueDate, today) < -graceDays;
}

/** Manual-pay bills due soon or overdue — excluded when autopay is enabled. */
export function manualBillNeedsAttention(
  dueDate: string,
  status: string,
  autopay: boolean,
  withinDays: number = BILL_DUE_SOON_DAYS,
  today: Date = new Date()
): boolean {
  if (autopay || status === 'paid') return false;
  return isBillDueSoon(dueDate, status, withinDays, today);
}

/** Autopay bills due today or within the next few days (not past due). */
export function autopayBillDueSoon(
  dueDate: string,
  status: string,
  withinDays: number = AUTOPAY_DUE_SOON_DAYS,
  today: Date = new Date()
): boolean {
  if (status === 'paid') return false;
  const diffDays = getBillDaysUntilDue(dueDate, today);
  return diffDays >= 0 && diffDays <= withinDays;
}

export function formatBillReminderMessage(billName: string, amount: number, timing: string): string {
  return `${billName} ($${amount.toFixed(2)}) — ${timing}`;
}

export function getBillRelativeTiming(
  dueDate: string,
  status: string,
  today: Date = new Date(),
  autopay = false
): string {
  if (status === 'paid') return 'Paid';
  const diffDays = getBillDaysUntilDue(dueDate, today);

  if (autopay) {
    if (diffDays < 0 && !isAutopayPastGrace(dueDate, AUTOPAY_GRACE_PERIOD_DAYS, today)) {
      return 'Autopay scheduled';
    }
    if (diffDays < 0) return 'Check autopay posted';
  } else if (status === 'overdue') {
    return 'Overdue';
  }

  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays > 1) return `Due in ${diffDays} days`;
  if (diffDays === -1) return 'Due yesterday';
  return `Overdue by ${Math.abs(diffDays)} days`;
}

export function getAutopayReviewMessage(billName: string, amount: number): string {
  return `${billName} ($${amount.toFixed(2)}) — autopay may not have posted, review`;
}

function parseBillDateLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

function formatBillDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Clamp day-of-month to the last day of the given calendar month. */
function dateWithDayOfMonth(year: number, monthIndex: number, dayOfMonth: number): Date {
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return new Date(year, monthIndex, Math.min(dayOfMonth, lastDay));
}

function usesDayOfMonth(frequency: string): boolean {
  return frequency === 'monthly' || frequency === 'quarterly' || frequency === 'yearly';
}

/** First occurrence on or after the anchor, honouring dayOfMonth when set. */
export function getFirstOccurrence(
  anchor: string,
  frequency: string,
  dayOfMonth?: number | null
): string {
  if (!dayOfMonth || !usesDayOfMonth(frequency)) return anchor;
  const a = parseBillDateLocal(anchor);
  const candidate = dateWithDayOfMonth(a.getFullYear(), a.getMonth(), dayOfMonth);
  const candidateStr = formatBillDateLocal(candidate);
  if (candidateStr >= anchor) return candidateStr;
  return advanceDueDate(candidateStr, frequency, dayOfMonth);
}

export function advanceDueDate(
  dueDate: string,
  frequency: string,
  dayOfMonth?: number | null
): string {
  const d = parseBillDateLocal(dueDate);
  switch (frequency) {
    case 'weekly':
      d.setDate(d.getDate() + 7);
      return formatBillDateLocal(d);
    case 'fortnightly':
      d.setDate(d.getDate() + 14);
      return formatBillDateLocal(d);
    case 'monthly':
    case 'quarterly':
    case 'yearly': {
      const monthsToAdd = frequency === 'monthly' ? 1 : frequency === 'quarterly' ? 3 : 12;
      const totalMonths = d.getFullYear() * 12 + d.getMonth() + monthsToAdd;
      const targetYear = Math.floor(totalMonths / 12);
      const targetMonth = totalMonths % 12;
      const day =
        dayOfMonth && usesDayOfMonth(frequency) ? dayOfMonth : d.getDate();
      return formatBillDateLocal(dateWithDayOfMonth(targetYear, targetMonth, day));
    }
    default:
      return dueDate;
  }
}

/** Next due date on or after `onOrAfter` (YYYY-MM-DD). */
export function getNextOccurrenceOnOrAfter(
  anchor: string,
  frequency: string,
  dayOfMonth: number | null | undefined,
  onOrAfter: string
): string {
  if (frequency === 'one_off') return anchor;

  let current = getFirstOccurrence(anchor, frequency, dayOfMonth);
  let guard = 0;
  while (current < onOrAfter && guard < 1200) {
    current = advanceDueDate(current, frequency, dayOfMonth);
    guard += 1;
  }
  return current;
}

/** Most recent due date on or before `onOrBefore`, or null if none yet. */
export function getLatestOccurrenceOnOrBefore(
  anchor: string,
  frequency: string,
  dayOfMonth: number | null | undefined,
  onOrBefore: string
): string | null {
  if (frequency === 'one_off') {
    return anchor <= onOrBefore ? anchor : null;
  }

  let current = getFirstOccurrence(anchor, frequency, dayOfMonth);
  if (current > onOrBefore) return null;

  let last = current;
  let guard = 0;
  while (guard < 1200) {
    const next = advanceDueDate(current, frequency, dayOfMonth);
    if (next > onOrBefore) break;
    last = next;
    current = next;
    guard += 1;
  }
  return last;
}

/** All occurrence ISO dates within an inclusive date range. */
export function getBillOccurrencesInRange(
  anchor: string,
  frequency: string,
  dayOfMonth: number | null | undefined,
  rangeStart: string,
  rangeEnd: string
): string[] {
  if (frequency === 'one_off') {
    return anchor >= rangeStart && anchor <= rangeEnd ? [anchor] : [];
  }

  const occurrences: string[] = [];
  let current = getNextOccurrenceOnOrAfter(anchor, frequency, dayOfMonth, rangeStart);
  let guard = 0;
  while (current <= rangeEnd && guard < 400) {
    occurrences.push(current);
    current = advanceDueDate(current, frequency, dayOfMonth);
    guard += 1;
  }
  return occurrences;
}

export type BillEffectiveDueInput = {
  dueDate: string;
  frequency: string;
  dayOfMonth?: number | null;
  lastPaidAt?: string | null;
};

function paymentWindowIso(dueDate: string): { from: string; to: string } {
  const due = parseBillDateLocal(dueDate);
  const from = new Date(due);
  from.setDate(from.getDate() - 7);
  const to = new Date(due);
  to.setDate(to.getDate() + AUTOPAY_GRACE_PERIOD_DAYS + 7);
  return { from: formatBillDateLocal(from), to: formatBillDateLocal(to) };
}

function isPaidForDueDate(lastPaidAt: string | null | undefined, dueDate: string): boolean {
  if (!lastPaidAt) return false;
  const paidDay = lastPaidAt.slice(0, 10);
  const { from, to } = paymentWindowIso(dueDate);
  return paidDay >= from && paidDay <= to;
}

/**
 * Effective next due for display/status.
 * Unpaid past cycles stay on the latest occurrence (overdue);
 * after payment for that cycle, advances to the following occurrence.
 */
export function getEffectiveDueDate(
  bill: BillEffectiveDueInput,
  today: Date = new Date()
): string {
  if (bill.frequency === 'one_off') return bill.dueDate;

  const todayStr = formatBillDateLocal(
    new Date(today.getFullYear(), today.getMonth(), today.getDate())
  );
  const latest = getLatestOccurrenceOnOrBefore(
    bill.dueDate,
    bill.frequency,
    bill.dayOfMonth,
    todayStr
  );
  const next = getNextOccurrenceOnOrAfter(
    bill.dueDate,
    bill.frequency,
    bill.dayOfMonth,
    todayStr
  );

  if (!latest) return next;

  if (isPaidForDueDate(bill.lastPaidAt, latest)) {
    return advanceDueDate(latest, bill.frequency, bill.dayOfMonth);
  }

  return latest;
}

export function computeBillStatus(dueDate: string, isPaid: boolean, today: Date = new Date()): 'upcoming' | 'due' | 'overdue' | 'paid' {
  if (isPaid) return 'paid';
  const diffDays = getBillDaysUntilDue(dueDate, today);
  if (diffDays < 0) return 'overdue';
  if (diffDays === 0) return 'due';
  return 'upcoming';
}

export const DEFAULT_ALERT_THRESHOLDS: AlertThresholds = {
  warning: 75,
  critical: 90,
  limit: 100,
};

/** Result of a savings-plan calculation (value or user-facing error). */
export type SavingsPlanCalcResult =
  | { value: number }
  | { value: string }
  | { error: string };

function roundToCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function parseDateOnly(dateStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Calendar months from start of today's month until target date (inclusive-ish).
 * Returns max(1, …) when the target is on or after today.
 * Returns 0 when the target date is in the past (caller should treat as error).
 */
export function countCalendarMonthsUntil(targetDate: string, today: Date = new Date()): number {
  const target = parseDateOnly(targetDate);
  if (!target) return 0;

  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const targetStart = new Date(target.getFullYear(), target.getMonth(), target.getDate());

  if (targetStart < todayStart) return 0;

  const months =
    (targetStart.getFullYear() - todayStart.getFullYear()) * 12 +
    (targetStart.getMonth() - todayStart.getMonth());

  // Same month (or later day in same month) counts as at least 1 month of saving.
  return Math.max(1, months || 1);
}

export function calculateAmountToSave(targetAmount: number, currentAmount: number = 0): number {
  return Math.max(0, targetAmount - currentAmount);
}

export function calculateMonthlyTargetFromPlan(params: {
  amountToSave: number;
  targetDate: string;
  today?: Date;
}): SavingsPlanCalcResult {
  const { amountToSave, targetDate, today } = params;
  if (!(amountToSave > 0) || !Number.isFinite(amountToSave)) {
    return { error: 'Enter a valid target amount greater than zero' };
  }
  if (!targetDate) {
    return { error: 'Enter a target date' };
  }
  const months = countCalendarMonthsUntil(targetDate, today);
  if (months === 0) {
    return { error: 'Target date must be in the future' };
  }
  return { value: roundToCents(amountToSave / months) };
}

export function calculateTargetAmountFromPlan(params: {
  monthlyTarget: number;
  targetDate: string;
  /** Already saved; total target = startingAmount + monthly × months */
  startingAmount?: number;
  today?: Date;
}): SavingsPlanCalcResult {
  const { monthlyTarget, targetDate, startingAmount = 0, today } = params;
  if (!(monthlyTarget > 0) || !Number.isFinite(monthlyTarget)) {
    return { error: 'Enter a valid monthly target greater than zero' };
  }
  if (!(startingAmount >= 0) || !Number.isFinite(startingAmount)) {
    return { error: 'Starting amount cannot be negative' };
  }
  if (!targetDate) {
    return { error: 'Enter a target date' };
  }
  const months = countCalendarMonthsUntil(targetDate, today);
  if (months === 0) {
    return { error: 'Target date must be in the future' };
  }
  const contributions = monthlyTarget * months;
  const amount = roundToCents(startingAmount + contributions);
  if (!(amount > 0)) {
    return { error: 'Computed target amount must be greater than zero' };
  }
  return { value: amount };
}

export function calculateTargetDateFromPlan(params: {
  amountToSave: number;
  monthlyTarget: number;
  today?: Date;
}): SavingsPlanCalcResult {
  const { amountToSave, monthlyTarget, today = new Date() } = params;
  if (!(amountToSave > 0) || !Number.isFinite(amountToSave)) {
    return { error: 'Enter a valid target amount greater than zero' };
  }
  if (!(monthlyTarget > 0) || !Number.isFinite(monthlyTarget)) {
    return { error: 'Enter a valid monthly target greater than zero' };
  }
  const monthsNeeded = Math.max(1, Math.ceil(amountToSave / monthlyTarget));
  const result = new Date(today.getFullYear(), today.getMonth() + monthsNeeded, today.getDate());
  const yyyy = result.getFullYear();
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return { value: `${yyyy}-${mm}-${dd}` };
}
